<template>
  <div
    class="bg-white overflow-auto padding-five-all"
    :class="{ 'margin-top-ten': !embedded }"
  >
    <b-container fluid>
      <b-row v-if="showHeader">
        <b-col cols="12">
          <h5 class="color-orange">{{ sectionTitle }}</h5>
        </b-col>
      </b-row>

      <b-row v-if="loadError">
        <b-col cols="12">
          <b-alert show variant="danger" class="mb-3">
            {{ loadError }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-table
            :key="fieldsSignature"
            class="table-fixed liquidacion-reportes-table"
            show-empty
            :empty-text="emptyText"
            small
            striped
            hover
            responsive
            :busy="loading"
            :items="items"
            :fields="tableFields"
            :tbody-tr-class="rowClass"
          >
            <template #table-busy>
              <div class="text-center text-danger my-3">
                <b-spinner class="align-middle mr-2" />
                <strong>{{ loadingText }}</strong>
              </div>
            </template>

            <template #cell(nroOs)="{ item, value }">
              <span :class="{ 'font-italic text-muted': item.esAdicional }">
                {{
                  item.esAdicional
                    ? `${value} · Adicional ${item.orden}`
                    : value
                }}
              </span>
            </template>

            <template #cell(estado)="{ item, value }">
              <span v-if="item.esAdicional" class="text-muted">—</span>
              <span v-else>{{ value }}</span>
            </template>

            <template #cell(penalidad)="row">
              <span
                v-if="showPenalidadTooltip && getPenalidadTooltip(row.item)"
                v-b-tooltip.hover
                :title="getPenalidadTooltip(row.item)"
                class="liquidacion-reportes-penalidad-tooltip"
              >
                {{ formatPenalidadValue(row.item.penalidad) }}
              </span>
              <span v-else>
                {{ formatPenalidadValue(row.item.penalidad) }}
              </span>
            </template>

            <template #cell(archivoPdf)="{ item }">
              <a
                v-if="hasPdf(item)"
                :href="getPdfUrl(item)"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-sm btn-link p-0 text-danger font-weight-bold"
                title="Ver PDF"
              >
                <font-awesome-icon icon="file-pdf" class="mr-1" />
                PDF
              </a>
              <span v-else class="text-muted">-</span>
            </template>

            <template #cell(urlPdf)="{ item }">
              <a
                v-if="hasPdf(item)"
                :href="getPdfUrl(item)"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-sm btn-link p-0 text-danger font-weight-bold"
                title="Ver PDF"
              >
                <font-awesome-icon icon="file-pdf" class="mr-1" />
                PDF
              </a>
              <span v-else class="text-muted">-</span>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <b-row v-if="showPagination && totalRows > perPage">
        <b-col cols="12">
          <b-pagination
            :value="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            pills
            align="center"
            size="md"
            class="my-3 liquidacion-reportes-pagination"
            @input="$emit('update:currentPage', $event)"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  formatMoney,
  formatPenalidadTooltip,
} from "@/services/liquidacion-reportes/liquidacion-reportes.formatters";
import { MESSAGES } from "@/services/liquidacion-reportes/liquidacion-reportes.constants";

const BLANK_ON_ADICIONAL_KEYS = new Set(["estado"]);

export default {
  name: "LiquidacionReportesTable",

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      required: true,
    },
    moneyColumns: {
      type: Array,
      default: () => [],
    },
    sectionTitle: {
      type: String,
      default: MESSAGES.RESUMEN_SECTION_TITLE,
    },
    emptyText: {
      type: String,
      default: MESSAGES.EMPTY_TABLE,
    },
    loadingText: {
      type: String,
      default: MESSAGES.LOADING,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadError: {
      type: String,
      default: "",
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    totalRows: {
      type: Number,
      default: 0,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    showPenalidadTooltip: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    formatPenalidadValue(value) {
      return formatMoney(value);
    },

    getPenalidadTooltip(item) {
      return formatPenalidadTooltip(item);
    },

    rowClass(item) {
      if (!item) return "";
      return item.esAdicional ? "table-success" : "";
    },

    hasPdf(item) {
      const url = item?.urlPdf || item?.archivoPdf;
      if (!url || typeof url !== "string") return false;
      const clean = url.trim();
      return (
        clean !== "" &&
        clean !== "-" &&
        clean.toLowerCase() !== "null" &&
        clean.toLowerCase() !== "undefined"
      );
    },

    getPdfUrl(item) {
      return item?.urlPdf || item?.archivoPdf || "";
    },
  },

  computed: {
    tableFields() {
      const moneySet = new Set(this.moneyColumns);

      return this.fields.map((field) => {
        if (field.key === "penalidad" && this.showPenalidadTooltip) {
          return field;
        }

        if (moneySet.has(field.key)) {
          return {
            ...field,
            formatter: (value) => formatMoney(value),
          };
        }

        if (BLANK_ON_ADICIONAL_KEYS.has(field.key)) {
          return {
            ...field,
            formatter: (value, key, item) => (item.esAdicional ? "" : value),
          };
        }

        if (field.key === "archivoPdf" || field.key === "urlPdf") {
          return {
            ...field,
            formatter: (value, key, item) => (this.hasPdf(item) ? value : "-"),
          };
        }

        return field;
      });
    },

    fieldsSignature() {
      return this.fields.map((field) => field.key).join("|");
    },
  },
};
</script>

<style>
.liquidacion-reportes-row-adicional > td {
  background-color: #e6f4ea !important;
}

.liquidacion-reportes-adicional-label {
  font-style: italic;
  color: #6c757d;
}
</style>
